import { useEffect, useContext, createContext, type Dispatch, type SetStateAction } from 'react';

/**
 * Context for communicating unsaved changes state from section pages
 * up to SectionLayout (which controls the "Back to Dashboard" button).
 */
export interface UnsavedChangesContextValue {
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: Dispatch<SetStateAction<boolean>>;
}

export const UnsavedChangesContext = createContext<UnsavedChangesContextValue>({
  hasUnsavedChanges: false,
  setHasUnsavedChanges: () => {},
});

/**
 * Hook for section pages to register unsaved changes.
 * Syncs the local `hasUnsavedChanges` flag to the SectionLayout context
 * and adds a `beforeunload` guard for browser close/refresh.
 */
export function useUnsavedChangesGuard(hasUnsavedChanges: boolean) {
  const { setHasUnsavedChanges } = useContext(UnsavedChangesContext);

  // Sync to context so SectionLayout can guard its "Back to Dashboard" button
  useEffect(() => {
    setHasUnsavedChanges(hasUnsavedChanges);
    return () => setHasUnsavedChanges(false);
  }, [hasUnsavedChanges, setHasUnsavedChanges]);

  // Guard against browser close / refresh
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [hasUnsavedChanges]);
}
